import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { eventService } from '../../services/api';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { accountZone, accountDate, dayKey, localTimeChoices, eventTimes, formatAccountTime } from '../../utils/accountTime';
import {
  CalendarIcon,
  ClockIcon,
  XMarkIcon,
  SparklesIcon,
  CheckIcon,
  BoltIcon,
  AdjustmentsHorizontalIcon
} from '@heroicons/react/24/outline';

const EventQuickCreateModal = ({ open, onClose, defaultDate }) => {
  const { user } = useAuth();
  const timezone = accountZone(user?.timezone);
  const [offset, setOffset] = useState('');
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [date, setDate] = useState(dayKey(defaultDate || new Date(), timezone));
  const [time, setTime] = useState('12:00');
  const [duration, setDuration] = useState(60);
  const [allDay, setAllDay] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [description, setDescription] = useState('');
  const [guestInput, setGuestInput] = useState('');
  const [guestEmails, setGuestEmails] = useState([]);
  const [guestError, setGuestError] = useState(null);

  const quickTemplates = [
    {
      id: 'standup',
      title: 'Daily Standup',
      suggestedTitle: 'Team Standup',
      time: '09:00',
      duration: 15,
      accent: 'from-blue-500/20 via-blue-500/10 to-blue-500/5',
      description: 'Fast status sync with the team'
    },
    {
      id: 'deepwork',
      title: 'Deep Work Block',
      suggestedTitle: 'Focus Session',
      time: '13:00',
      duration: 90,
      accent: 'from-purple-500/20 via-purple-500/10 to-purple-500/5',
      description: 'Heads-down focus time'
    },
    {
      id: 'coffee',
      title: 'Coffee Catch-up',
      suggestedTitle: 'Coffee Catch-up ☕️',
      time: '16:00',
      duration: 30,
      accent: 'from-rose-500/20 via-rose-500/10 to-rose-500/5',
      description: 'Quick relationship touchpoint'
    }
  ];

  // Smart time suggestions based on current time
  const getSmartTimeSuggestions = () => {
    const now = new Date();
    const currentHour = dayKey(now, timezone) === date ? accountDate(now, timezone).hour : -1;
    const suggestions = [];
    
    if (currentHour < 9) {
      suggestions.push({ label: '9:00 AM', value: '09:00', desc: 'Morning start' });
    }
    if (currentHour < 12) {
      suggestions.push({ label: '12:00 PM', value: '12:00', desc: 'Lunch time' });
    }
    if (currentHour < 15) {
      suggestions.push({ label: '3:00 PM', value: '15:00', desc: 'Afternoon' });
    }
    if (currentHour < 18) {
      suggestions.push({ label: '6:00 PM', value: '18:00', desc: 'Evening' });
    }
    
    return suggestions;
  };

  const createMutation = useMutation({
    mutationFn: async () => {
      const times = eventTimes({date, time, duration, allDay, offset}, timezone);
      const event = await eventService.createEvent({
        title,
        description: description.trim() || undefined,
        ...times,
      });

      if (guestEmails.length > 0) {
        try {
          await eventService.updateEvent(event.id, {
            enhancement_metadata: {
              guests: guestEmails,
            },
          });
        } catch (error) {
          console.error('Failed to attach guests', error);
          toast.error('Event created, but guest invites could not be attached.');
        }
      }

      return event;
    },
    onSuccess: () => {
      toast.success('🎉 Event created successfully!', {
        duration: 3000,
        icon: '✅',
      });
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      queryClient.invalidateQueries({ queryKey: ['today'] });
      onClose?.();
      setTitle('');
      setDescription('');
      setGuestInput('');
      setGuestEmails([]);
      setGuestError(null);
    },
    onError: (e) => toast.error(e.response?.data?.detail || e.message || 'Failed to create event')
  });

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setDate(dayKey(defaultDate || new Date(), timezone));
      setTime(defaultDate && !/^\d{4}-\d{2}-\d{2}$/.test(String(defaultDate)) ? accountDate(defaultDate, timezone).toFormat('HH:mm') : '12:00');
    }
    if (open) {
      setDescription('');
      setGuestInput('');
      setGuestEmails([]);
      setGuestError(null);
    }
  }, [open, defaultDate, timezone]);
  useEffect(() => setOffset(''), [date, time, timezone, allDay]);
  const choices = allDay ? [] : localTimeChoices(date, time, timezone);
  let timeError = '';
  let preview;
  try { preview = eventTimes({date, time, duration, allDay, offset}, timezone); } catch (error) { timeError = error.message; }

  const handleGuestAdd = (value) => {
    const trimmed = value.trim();
    if (!trimmed) {
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) {
      setGuestError('Please enter a valid email address.');
      return;
    }

    const normalized = trimmed.toLowerCase();
    if (guestEmails.includes(normalized)) {
      setGuestError('Guest already added.');
      return;
    }

    setGuestEmails((prev) => [...prev, normalized]);
    setGuestInput('');
    setGuestError(null);
  };

  const handleGuestKeyDown = (event) => {
    if ((event.key === 'Enter' || event.key === ',' || event.key === 'Tab') && guestInput.trim()) {
      event.preventDefault();
      handleGuestAdd(guestInput);
    }
  };

  const handleGuestPaste = (event) => {
    const clipboard = event.clipboardData.getData('text');
    if (clipboard.includes(',')) {
      event.preventDefault();
      clipboard.split(',').forEach(segment => handleGuestAdd(segment));
    }
  };

  if (!open) return null;
  
  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-[#101813]/45 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute left-0 right-0 bottom-0 px-4 pb-6">
        <div className="modal-safe animate-slide-up relative mx-auto flex max-h-[calc(100vh-32px)] max-w-screen-md overflow-hidden rounded-lg border border-[#dce3dd] bg-white shadow-[0_20px_60px_rgba(21,36,28,0.18)]">
          <div className="relative flex-1 overflow-y-auto px-4 pb-6 pt-5 sm:px-6">
            {/* Drag handle */}
            <div className="mx-auto mb-5 h-1 w-10 rounded-full bg-[#d9dfda]" />

            {/* Enhanced Header */}
            <div className="flex items-start justify-between gap-4 text-[#17211c]">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#e2edf6] text-[#32699a]">
                  <CalendarIcon className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="text-lg font-bold">Create event</h2>
                  <p className="text-sm text-[#6d7871]">Times in {timezone}. Add a confirmed commitment to your calendar.</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="icon-button"
                aria-label="Close"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="mt-6 space-y-6">
              <section className="rounded-lg border border-[#dce3dd] bg-[#fbfcfb] p-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold text-gray-900">
                    Event Title
                  </label>
                  <span className="text-xs text-gray-500">Keep it action-oriented</span>
                </div>
                <input
                  aria-label="Event title"
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="e.g. Design Review with Product"
                  className="mt-3 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm font-medium text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-400/60"
                />

                <div className="mt-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Smart templates</p>
                  <div className="mt-2 grid gap-3 sm:grid-cols-3">
                    {quickTemplates.map(template => (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => {
                          setTitle(template.suggestedTitle);
                          setTime(template.time);
                          setDuration(template.duration);
                          setAllDay(false);
                        }}
                        className={`group relative overflow-hidden rounded-2xl border border-transparent bg-gradient-to-br ${template.accent} p-3 text-left transition hover:-translate-y-0.5 hover:border-blue-300/60`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-sm font-semibold text-gray-900 group-hover:text-blue-900">{template.title}</p>
                            <p className="text-xs text-gray-500 group-hover:text-gray-600">{template.description}</p>
                          </div>
                          <SparklesIcon className="h-5 w-5 text-blue-500/80" />
                        </div>
                        <div className="mt-3 flex items-center gap-2 text-xs text-gray-600">
                          <ClockIcon className="h-4 w-4 text-blue-500/70" />
                          <span>{template.time}</span>
                          <span>·</span>
                          <span>{template.duration} min</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </section>

              <section className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Description
                  </label>
                  <span className="text-xs text-gray-400">Optional</span>
                </div>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Share agenda, context, or prep notes for your guests."
                  rows={4}
                  className="mt-3 w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm leading-5 text-gray-900 shadow-inner focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-400/60"
                />
                <div className="mt-2 text-xs text-gray-400">Rich formatting available after saving via the full editor.</div>
              </section>

              <section className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">Date</label>
                    <div className="mt-2 flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-400/50">
                      <CalendarIcon className="h-5 w-5 text-blue-500" />
                      <input
                        aria-label="Date"
                        type="date"
                        value={date}
                        onChange={e => setDate(e.target.value)}
                        className="w-full border-none bg-transparent text-sm font-medium text-gray-900 focus:outline-none"
                      />
                    </div>
                  </div>

                  {!allDay && (
                    <div className="relative">
                      <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">Start time</label>
                      <div className="mt-2 flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-400/50">
                        <ClockIcon className="h-5 w-5 text-blue-500" />
                        <input
                          aria-label="Start time"
                          type="time"
                          value={time}
                          onChange={e => setTime(e.target.value)}
                          className="w-full border-none bg-transparent text-sm font-medium text-gray-900 focus:outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => setShowSuggestions(prev => !prev)}
                          className="rounded-full bg-blue-100/60 p-1 text-blue-600 transition hover:bg-blue-200/80"
                          aria-label="Show smart time suggestions"
                        >
                          <SparklesIcon className="h-4 w-4" />
                        </button>
                      </div>

                      {showSuggestions && (
                        <div className="absolute inset-x-0 z-10 mt-2 space-y-1 rounded-2xl border border-blue-200 bg-white p-3 shadow-lg">
                          <p className="text-xs font-semibold text-blue-700">Smart Suggestions</p>
                          {getSmartTimeSuggestions().map((suggestion, index) => (
                            <button
                              key={index}
                              type="button"
                              onClick={() => {
                                setTime(suggestion.value);
                                setShowSuggestions(false);
                              }}
                              className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-xs text-blue-800 transition hover:bg-blue-50"
                            >
                              <span className="font-medium">{suggestion.label}</span>
                              <span className="text-blue-500/80">{suggestion.desc}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {!allDay && choices.length > 1 && <label className="mt-3 block text-sm">
                  This time occurs twice. Choose UTC offset
                  <select aria-label="UTC offset" className="ml-2 rounded border p-2" value={offset} onChange={e => setOffset(e.target.value)}>
                    <option value="">Choose occurrence</option>
                    {choices.map(choice => <option key={choice.offset} value={String(choice.offset)}>{choice.toFormat('ZZZZ (ZZ)')}</option>)}
                  </select>
                </label>}
                {!allDay && (
                  <div className="mt-4">
                    <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">Duration</label>
                    <div className="mt-2 grid gap-3 sm:grid-cols-[1fr_minmax(0,1fr)]">
                      <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-400/50">
                        <AdjustmentsHorizontalIcon className="h-5 w-5 text-blue-500" />
                        <input
                          aria-label="Duration in minutes"
                          type="number"
                          min="15"
                          step="15"
                          value={duration}
                          onChange={e => setDuration(Number(e.target.value))}
                          className="w-full border-none bg-transparent text-sm font-medium text-gray-900 focus:outline-none"
                          placeholder="Duration (min)"
                        />
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {[30, 45, 60, 90, 120].map(dur => (
                          <button
                            key={dur}
                            type="button"
                            onClick={() => setDuration(dur)}
                            className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                              duration === dur
                                ? 'bg-blue-600 text-white shadow-sm'
                                : 'bg-white text-gray-600 ring-1 ring-gray-200 hover:text-gray-900'
                            }`}
                          >
                            {dur}m
                          </button>
                        ))}
                      </div>
                    </div>
                    <p className="mt-2 text-xs text-gray-500">
                      Ends at {preview ? formatAccountTime(preview.end_time, timezone, 'ccc, MMM d, h:mm a ZZZZ') : '—'}
                    </p>
                  </div>
                )}

                <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3">
                  <div className="flex items-center gap-3">
                    <span className={`flex h-10 w-10 items-center justify-center rounded-xl border ${allDay ? 'border-blue-200 bg-blue-50 text-blue-600' : 'border-gray-200 bg-white text-gray-500'}`}>
                      <BoltIcon className="h-5 w-5" />
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">All day experience</p>
                      <p className="text-xs text-gray-500">Perfect for offsites, travel, or focus days</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setAllDay(prev => !prev)}
                    className={`flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold transition ${
                      allDay ? 'bg-blue-600 text-white shadow-sm' : 'bg-white text-gray-600 ring-1 ring-gray-200'
                    }`}
                  >
                    {allDay ? 'All day enabled' : 'Enable all day'}
                  </button>
                </div>
              </section>

              <section className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="flex items-center gap-2 text-sm font-semibold text-blue-900">
                        <SparklesIcon className="h-4 w-4 text-blue-600" />
                        Add Guests
                      </h3>
                      <p className="mt-1 text-xs text-blue-700/80">Invite collaborators and keep them in the loop.</p>
                    </div>
                    <span className="rounded-full bg-white/70 px-2 py-1 text-[11px] font-semibold text-blue-700">
                      Optional
                    </span>
                  </div>

                  <div className="mt-3 rounded-xl border border-blue-200 bg-white p-2 focus-within:ring-2 focus-within:ring-blue-300">
                    <div className="flex flex-wrap items-center gap-2">
                      {guestEmails.map((guest) => (
                        <span
                          key={guest}
                          className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700"
                        >
                          {guest}
                          <button
                            type="button"
                            onClick={() => setGuestEmails((prev) => prev.filter((email) => email !== guest))}
                            className="text-blue-500 transition hover:text-blue-700"
                            aria-label={`Remove ${guest}`}
                          >
                            <XMarkIcon className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                      <input
                        value={guestInput}
                        onChange={(e) => {
                          setGuestInput(e.target.value);
                          if (guestError) setGuestError(null);
                        }}
                        onKeyDown={handleGuestKeyDown}
                        onPaste={handleGuestPaste}
                        placeholder={guestEmails.length === 0 ? 'Add guest email…' : 'Add another guest'}
                        className="flex-1 min-w-[160px] border-none bg-transparent px-2 py-1 text-sm text-blue-900 placeholder:text-blue-300 focus:outline-none"
                      />
                    </div>
                  </div>
                  {guestError && <div className="mt-2 text-xs font-semibold text-rose-600">{guestError}</div>}
                  {guestEmails.length === 0 && !guestError && (
                    <div className="mt-2 text-xs text-blue-600/80">Press Enter after each email or paste a comma-separated list.</div>
                  )}
                </div>
                {/* <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                  <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                    <MapPinIcon className="h-4 w-4 text-blue-500" />
                    Location (optional)
                  </h3>
                  <p className="mt-2 text-xs text-gray-500">Add later in the full editor if needed.</p>
                </div> */}
                <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                  <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                    <SparklesIcon className="h-4 w-4 text-purple-500" />
                    AI Assist
                  </h3>
                  <p className="mt-2 text-xs text-gray-500">Need a richer agenda? Promote this event to the AI planner from the calendar later.</p>
                </div>
              </section>
            </div>

            {timeError && <p role="alert" className="mt-4 text-sm text-rose-700">{timeError}</p>}
            {/* Action Buttons */}
            <div className="mt-8 flex flex-col gap-3 border-t border-gray-100 pt-4 sm:flex-row sm:justify-end">
              <button
                onClick={onClose}
                className="inline-flex items-center justify-center rounded-xl border border-gray-200 px-5 py-3 text-sm font-semibold text-gray-600 transition hover:border-gray-300 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => createMutation.mutate()}
                disabled={!title.trim() || !!timeError || createMutation.isPending}
                className="primary-button disabled:cursor-not-allowed disabled:opacity-60"
              >
                {createMutation.isPending ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    <span>Creating…</span>
                  </>
                ) : (
                  <>
                    <CheckIcon className="h-4 w-4" />
                    <span>Create Event</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Animation Styles */}
      <style jsx>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default EventQuickCreateModal;
